import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, AuftragsVolumen as Sicht } from "@kundeninfo/api-client";
import { formatBetrag } from "../format.js";
import { Hinweis, Tabelle } from "@kundeninfo/ui/me-design";
import "./AuftragsVolumen.css";

/**
 * DER LESEKASTEN „Volumen je Jahr" am Auftragsdetail (Spec, „Volumen je Kalenderjahr").
 *
 * Dieselben Zahlen, die die Liquiditätsplanung (Teilprojekt 5) später benutzt — deshalb
 * steht hier keine eigene Rechnung, sondern nur die Darstellung dessen, was
 * `GET …/volumen` liefert.
 *
 * **KALENDERjahre, nicht Leistungsjahre** — bei einem Vertrag über Vertragsjahre sind das
 * verschiedene Achsen; die Spalte heisst deshalb „Jahr" und nicht „Leistungsjahr".
 *
 * **Eigener CSS-Block `vol__`.**
 */

export type VolumenClient = Pick<ApiClient, "getVolumen">;

export type AuftragsVolumenProps = {
  client: VolumenClient;
  customerId: string;
  orderId: string;
  currency?: string;
};

export function AuftragsVolumen(props: AuftragsVolumenProps): ReactElement {
  const { client, customerId, orderId, currency = "EUR" } = props;
  const [sicht, setSicht] = useState<Sicht | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const lauf = useRef(0);
  const getVolumen = client.getVolumen;

  useEffect(() => {
    const meiner = ++lauf.current;
    setSicht(null);
    setFehler(null);
    getVolumen(customerId, orderId)
      .then((antwort) => {
        if (lauf.current !== meiner) return;
        setSicht(antwort);
      })
      .catch(() => {
        if (lauf.current !== meiner) return;
        setFehler("Das Volumen konnte nicht geladen werden.");
      });
    return () => { lauf.current++; };
  }, [getVolumen, customerId, orderId]);

  if (fehler !== null) return <Hinweis art="fehler">{fehler}</Hinweis>;
  if (sicht === null) return <p className="vol__laedt">Das Volumen wird geladen …</p>;

  return (
    <section className="vol" aria-label="Volumen je Jahr">
      <h3 className="vol__titel">Volumen je Jahr</h3>
      {sicht.jahre.length === 0 && (
        <p className="vol__hinweis">Ohne Laufzeit und Positionen gibt es kein Volumen.</p>
      )}
      {sicht.jahre.length > 0 && (
        /* me-tabelle (ME-Etappe 3, Aufgabe 4): Rahmen, Zebra, Rollen; Beträge rechtsbündig. */
        <Tabelle>
          <table className="vol__tabelle">
            <thead>
              <tr>
                <th scope="col">Jahr</th>
                <th scope="col" data-ausrichtung="rechts">Auftragsvolumen</th>
                <th scope="col" data-ausrichtung="rechts">Gewichtet</th>
                <th scope="col" data-ausrichtung="rechts">Abgerechnet</th>
                <th scope="col" data-ausrichtung="rechts">Bezahlt</th>
                <th scope="col" data-ausrichtung="rechts">Offen</th>
              </tr>
            </thead>
            <tbody>
              {sicht.jahre.map((jahr) => (
                <tr key={jahr.jahr}>
                  <td>{jahr.jahr}</td>
                  <td className="ki-num" data-ausrichtung="rechts">
                    {formatBetrag(Number(jahr.auftragsvolumen), currency)}
                  </td>
                  <td className="ki-num" data-ausrichtung="rechts" data-testid={`vol-${jahr.jahr}-gewichtet`}>
                    {formatBetrag(Number(jahr.auftragsvolumenGewichtet), currency)}
                  </td>
                  <td className="ki-num" data-ausrichtung="rechts">
                    {formatBetrag(Number(jahr.abgerechnetesVolumen), currency)}
                  </td>
                  <td className="ki-num" data-ausrichtung="rechts">
                    {formatBetrag(Number(jahr.bezahltesVolumen), currency)}
                  </td>
                  <td className="ki-num" data-ausrichtung="rechts" data-testid={`vol-${jahr.jahr}-offen`}>
                    {formatBetrag(Number(jahr.offenesVolumen), currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Tabelle>
      )}
      <p className="vol__fuss">
        Gewichtet nach der Abrechnungswahrscheinlichkeit der Auftragspositionen; die Zahlen
        sind Netto-Beträge und dieselben, die die Liquiditätsplanung nutzt.
      </p>
    </section>
  );
}
